import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConnectedProps } from "react-redux";
import { Button, Card, Empty, Modal, Skeleton, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";

import connector from "./Container";
import FilterButtons from "../../../../shared/FilterButtons";
import { CourseStatistics } from "../../../../../utils";
import CircleProgress from "../../../../shared/CircleProgress";
import { ReactComponent as CourseIcon } from "../../../../../assets/images/reusable-items/course.svg";
import { ReactComponent as DurationIcon } from "../../../../../assets/images/reusable-items/duration.svg";
import { ReactComponent as LessonIcon } from "../../../../../assets/images/reusable-items/lesson.svg";

const { Text } = Typography;

const UserCourseProgress = (props: ConnectedProps<typeof connector>) => {
  const {
    userCourse,
    loading,
    actions: { getUserDetailsStatistics },
  } = props;
  const { id } = useParams();

  const [filteredCourses, setFilteredCourses] = useState(userCourse);
  const [filter, setFilter] = useState("ALL");
  const filterProps = [
    { key: "All Courses", value: "ALL" },
    { key: "In Progress", value: "UNFINISHED" },
    { key: "Finished", value: "FINISHED" },
  ];
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLessons, setModalLessons] = useState<any>([]);

  useEffect(() => {
    id && getUserDetailsStatistics({ type: "course", userId: id });
  }, []);

  useEffect(() => {
    let filtered = userCourse;

    if (filter === "FINISHED") {
      filtered = filtered.filter(
        (course: CourseStatistics) => course.progress === 100
      );
    } else if (filter === "UNFINISHED") {
      filtered = filtered.filter(
        (course: CourseStatistics) => course.progress !== 100
      );
    }

    setFilteredCourses(filtered);
  }, [filter, userCourse]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
  };

  const openModal = (lessons: any) => {
    setModalLessons(lessons);
    setIsModalVisible(true);
  };

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <>
      <div>
        <div className="flex justify-between items-center">
          <Text className="text-xl text-primary-700 font-semibold">
            Course Progress
          </Text>
          <Link
            to={`/educator/user/${id}/details`}
            className="text-sm font-semibold text-primary-700"
          >
            {"< Back to User Details"}
          </Link>
        </div>
        <FilterButtons
          selectedFilter={filter}
          handleFilterChange={handleFilterChange}
          filterOptions={filterProps}
        />
        <div className="flex flex-wrap gap-5 mt-4 overflow-y-scroll max-h-[calc(100vh-265px)]">
          {filteredCourses?.length > 0 ? (
            filteredCourses.map((content: CourseStatistics, index: number) => (
              <Card key={index} className="w-[calc(33.33%-14px)] p-4">
                <div className="flex gap-4">
                  <div className="flex items-center">
                    <CircleProgress size={100} percentage={content.progress} />
                  </div>
                  <div className="w-full">
                    <div className="text-neutral-600 font-semibold min-h-12 line-clamp-2">
                      {content.courseTitle}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4">
                        <DurationIcon className="w-full h-full fill-primary-400" />
                      </div>

                      <Text className="text-sm text-neutral-500">
                        {content?.courseDuration ?? "0 hr 0 min"}
                      </Text>
                    </div>
                    <div className="border-b border-primary-300 my-4"></div>
                    <div className="flex gap-2 items-center ">
                      <div className="w-4 h-4">
                        <LessonIcon className="w-full h-full fill-primary-400" />
                      </div>
                      <Text className="text-sm text-neutral-600">
                        {content.remainingLessons === "0" ? (
                          <>Finished</>
                        ) : content.remainingLessons === "1" ? (
                          <>1 Lesson remain</>
                        ) : (
                          <>{content.remainingLessons} Lessons remain</>
                        )}
                      </Text>
                    </div>
                    <div className="flex gap-2 mt-4 ">
                      <Text
                        className={`w-[40%]  h-[30px] text-xs text-center bg-rose-400 text-white rounded-lg flex justify-center items-center`}
                      >
                        {content.pointsCompleted == content.totalPoints ? (
                          <>{content.totalPoints} P</>
                        ) : (
                          <>
                            {content.pointsCompleted}/{content.totalPoints} P
                          </>
                        )}
                      </Text>

                      <Button
                        onClick={() => openModal(content?.lessons)}
                        className={`w-[60%] h-[30px] bg-primary-600 text-white rounded-lg text-[12px]`}
                      >
                        View Lessons
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="flex items-center w-full justify-center h-full ">
              <Card className="shadow-md p-[60px] my-[40px]">
                <div className="flex flex-col gap-10 items-center justify-center">
                  <div className="h-[60px]">
                    <CourseIcon className="w-full h-full fill-primary-400" />
                  </div>
                  <Text className="text-neutral-600 text-xl font-bold text-center">
                    There are currently no
                    <div>available Courses</div>
                  </Text>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        closeIcon={null}
      >
        {modalLessons.length > 0 ? (
          <>
            <div className="flex justify-between items-center pb-4 mb-4 border-b border-b-neutral-200">
              <div className="flex gap-3 items-center">
                <LessonIcon className="fill-primary-400 w-[20px] h-[20px]" />
                <Text className="text-neutral-400 font-normal text-sm">
                  Lesson(s)
                </Text>
              </div>
            </div>
            <div className="flex flex-col max-h-[calc(100vh-300px)] overflow-y-auto">
              {modalLessons.map((lesson: any) => (
                <div
                  key={lesson?.lessonId}
                  className={`flex items-center px-4 py-3 mb-2 rounded-lg  ${
                    lesson?.isCompleted ? "bg-primary-100" : "bg-neutral-100"
                  } `}
                >
                  <Text
                    className={`flex-1 text-neutral-600 text-sm ${
                      lesson?.isCompleted ? "font-semibold" : ""
                    }`}
                  >
                    {lesson?.lessonTitle}
                  </Text>
                  {lesson?.isCompleted && (
                    <CheckOutlined className="text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <Empty className="py-5" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Modal>
    </>
  );
};

export default UserCourseProgress;
